import React, { useState } from 'react';
import FormulaireRepartition from './components/FormulaireRepartition';
import ResultatRepartition from './components/ResultatRepartition';
import HistoriqueSessions from './components/HistoriqueSessions';
import GestionDecoupages from './components/GestionDecoupages';
import { repartir, fmt } from './utils/calcul';
import { exportPDF, getPDFBlob } from './utils/pdfExport';
import { exportExcel } from './utils/excelExport';
import { Session, Decoupage, Part } from './types';
import './App.css';

type Onglet = 'calcul' | 'decoupages';

export default function App() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [sessionActive, setSessionActive] = useState<Session | null>(null);
  const [exporting, setExporting] = useState(false);
  const [sharing, setSharing]     = useState(false);
  const [decoupages, setDecoupages] = useState<Decoupage[]>([]);
  const [onglet, setOnglet] = useState<Onglet>('calcul');

  function handleCalculer(montant: number, parts: Part[], base: number, zone: string, limitePart: number, controleur: string): string | null {
    const resultats = repartir(montant, parts, base);
    const violation = resultats.find(r => r.part >= limitePart);
    if (violation) {
      const nom = violation.nom || `Personne ${violation.index + 1}`;
      return `La part de "${nom}" (${violation.part.toLocaleString('fr-FR')} FCFA) dépasse la limite de ${limitePart.toLocaleString('fr-FR')} FCFA par bénéficiaire. Ajustez le montant ou le nombre de parts.`;
    }
    const nouvelleSession: Session = {
      id: Date.now(),
      montant,
      parts,
      base,
      resultats,
      date: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      zone,
      controleur,
    };
    setSessions(prev => [nouvelleSession, ...prev]);
    setSessionActive(nouvelleSession);
    return null;
  }

  function handleExportExcel(): void {
    if (sessions.length === 0) return;
    exportExcel(sessions);
  }

  function handleExportPDF(): void {
    if (sessions.length === 0) return;
    setExporting(true);
    setTimeout(() => {
      exportPDF(sessions, decoupages);
      setExporting(false);
    }, 100);
  }

  async function handleShareWhatsApp(): Promise<void> {
    if (sessions.length === 0 || sharing) return;
    setSharing(true);
    try {
      const now     = new Date();
      const dateStr = now.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const timeStr = now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

      const lines: string[] = [
        `📋 *Répartition de montants*`,
        `Partagé le ${dateStr} à ${timeStr}`,
        '',
      ];
      sessions.forEach((s, i) => {
        const total = s.resultats.reduce((acc, r) => acc + r.part, 0);
        lines.push(`*N° ${i + 1}${s.zone ? ` — ${s.zone}` : ''}*`);
        if (s.controleur) lines.push(`Contrôleur : ${s.controleur}`);
        lines.push(`Montant : ${fmt(s.montant)} FCFA`);
        lines.push(`Parts : ${s.parts.length}`);
        lines.push(`Arrondi : Multiple de ${s.base}`);
        lines.push(`Total vérifié : ${fmt(total)} FCFA ✅`);
        lines.push('');
      });
      const text = lines.join('\n');

      const blob = getPDFBlob(sessions, decoupages);
      const fileName = `repartition_${dateStr.replace(/\//g, '-')}_${timeStr.replace(':', 'h')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });

      if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'Répartition de montants', text });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
      }
    } catch {
      // l'utilisateur a annulé ou le navigateur ne supporte pas
    } finally {
      setSharing(false);
    }
  }

  function handleSupprimerSession(id: number): void {
    setSessions(prev => prev.filter(s => s.id !== id));
    if (sessionActive && sessionActive.id === id) {
      setSessionActive(sessions.find(s => s.id !== id) ?? null);
    }
  }

  function handleAjouterDecoupage(d: Decoupage): void {
    setDecoupages(prev => [...prev, d]);
  }

  function handleSupprimerDecoupage(id: number): void {
    setDecoupages(prev => prev.filter(d => d.id !== id));
  }

  function handleModifierDecoupage(d: Decoupage): void {
    setDecoupages(prev => prev.map(x => x.id === d.id ? d : x));
  }

  return (
    <div className="app-layout">
      {/* ── Header ── */}
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <div className="header-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
            </div>
            <div>
              <h1 className="header-title">Répartition de montants</h1>
              <p className="header-sub">Arrondi au multiple de 50 · Total garanti exact</p>
            </div>
          </div>
          {sessions.length > 0 && onglet === 'calcul' && (
            <div className="header-actions">
              <button className="btn-excel" onClick={handleExportExcel}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="8" y1="13" x2="16" y2="13"/>
                  <line x1="8" y1="17" x2="16" y2="17"/>
                  <line x1="8" y1="9" x2="10" y2="9"/>
                </svg>
                Excel
              </button>
              <button className="btn-whatsapp" onClick={handleShareWhatsApp} disabled={sharing}>
                {sharing ? <span className="btn-spinner" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} /> : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                )}
                {sharing ? 'Partage…' : 'Partager'}
              </button>
              <button className="btn-pdf" onClick={handleExportPDF} disabled={exporting}>
                {exporting ? (
                  <span className="btn-spinner" />
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                )}
                {exporting ? 'Génération…' : `Exporter PDF (${sessions.length})`}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Navigation ── */}
      <nav className="app-nav">
        <div className="app-nav-inner">
          <button className={`nav-tab ${onglet === 'calcul' ? 'active' : ''}`} onClick={() => setOnglet('calcul')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            Calculer
            {sessions.length > 0 && <span className="nav-tab-count">{sessions.length}</span>}
          </button>
          <button className={`nav-tab ${onglet === 'decoupages' ? 'active' : ''}`} onClick={() => setOnglet('decoupages')}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Découpages horaires
            {decoupages.length > 0 && <span className="nav-tab-count">{decoupages.length}</span>}
          </button>
        </div>
      </nav>

      {/* ── Main ── */}
      <main className="app-main">
        {onglet === 'calcul' && (
          <div className="app-grid">
            <div className="col-left">
              <FormulaireRepartition onCalculer={handleCalculer} />
              {sessions.length > 0 && (
                <HistoriqueSessions
                  sessions={sessions}
                  sessionActive={sessionActive}
                  onSelect={setSessionActive}
                  onSupprimer={handleSupprimerSession}
                  onExportPDF={handleExportPDF}
                />
              )}
            </div>
            <div className="col-right">
              {sessionActive ? (
                <ResultatRepartition session={sessionActive} decoupages={decoupages} />
              ) : (
                <div className="empty-state">
                  <div className="empty-icon" aria-hidden="true">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                  </div>
                  <h2>Aucun calcul encore</h2>
                  <p>Renseignez le montant et le nombre de parts, puis cliquez sur <strong>Calculer</strong>.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {onglet === 'decoupages' && (
          <GestionDecoupages
            decoupages={decoupages}
            onAjouter={handleAjouterDecoupage}
            onSupprimer={handleSupprimerDecoupage}
            onModifier={handleModifierDecoupage}
          />
        )}
      </main>
    </div>
  );
}
